/**
 * Las asignaturas del profesor: la carpeta con la que organiza su material.
 *
 * Antes esta pantalla se quedaba en «Cargando asignaturas…» para siempre con cualquier fallo de
 * red, porque la promesa no tenía `catch`. Y las tres acciones que cambian algo —crear, archivar
 * y borrar— se invocaban sin capturar el fallo, así que cuando alguna no salía no pasaba nada de
 * nada: ni mensaje, ni deshacer, ni rastro.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SessionProfile } from '@clica/shared';
import {
  archiveSubject,
  countQuizzesIn,
  createSubject,
  deleteSubject,
  listSubjects,
  type Subject,
} from '../content/api.js';
import { useAvisos } from '../components/sistema/avisos.js';
import { Boton } from '../components/sistema/boton.js';
import { MarcaAsignatura } from '../components/sistema/marca-asignatura.js';
import { DialogoConfirmacion } from '../components/sistema/dialogo-confirmacion.js';
import { Glifo } from '../components/sistema/glifo.js';
import { Marco } from '../components/sistema/marco.js';
import { Rotulo } from '../components/sistema/rotulo.js';
import { useRecurso, VistaRecurso } from '../components/sistema/recurso.js';

/** Colores de asignatura, tomados de la paleta segura para daltonismo de la plataforma. */
const COLORS = ['#0072B2', '#D55E00', '#009E73', '#E69F00', '#6D3BF0', '#CC79A7'] as const;

function FormularioNuevo({
  ownerId,
  onCreated,
  onCancel,
}: {
  ownerId: string;
  onCreated: (subject: Subject) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(COLORS[0]);
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (name.trim() === '') return;

    setSaving(true);
    try {
      onCreated(await createSubject(ownerId, name.trim(), color));
    } catch (error) {
      // Antes esto era un `finally` sin `catch`: el botón se desbloqueaba y no ocurría nada más.
      console.error('No se ha podido crear la asignatura', error);
      avisos.fallo(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="rounded-card border-line border-2 p-5">
      <div className="flex flex-wrap items-end gap-5">
        <div className="min-w-48 flex-1">
          <Rotulo htmlFor="nombre-asignatura" className="mb-1 block">
            {t('subjects.name')}
          </Rotulo>
          <input
            id="nombre-asignatura"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5"
          />
        </div>

        <fieldset>
          <legend className="mb-1">
            <Rotulo>{t('subjects.color')}</Rotulo>
          </legend>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((option) => (
              <label key={option} className="cursor-pointer">
                <input
                  type="radio"
                  name="color"
                  value={option}
                  checked={color === option}
                  onChange={() => setColor(option)}
                  className="peer sr-only"
                />
                {/* Elegido = marca dibujada + borde grueso, no solo color. */}
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: option }}
                  className={[
                    'rounded-chip flex h-11 w-11 items-center justify-center border-2 text-white',
                    color === option ? 'border-edge' : 'border-transparent',
                    'peer-focus-visible:outline-focus-out peer-focus-visible:outline-3',
                  ].join(' ')}
                >
                  {color === option ? <Glifo nombre="correcto" size={20} /> : null}
                </span>
                <span className="sr-only">{option}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex gap-2">
          <Boton type="submit" variante="primaria" cargando={saving}>
            {t('subjects.save')}
          </Boton>
          <Boton variante="secundaria" onClick={onCancel}>
            {t('subjects.cancel')}
          </Boton>
        </div>
      </div>
    </form>
  );
}

function Tarjeta({
  subject,
  onChanged,
  retraso = 0,
}: {
  subject: Subject;
  onChanged: () => void;
  retraso?: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const archived = subject.archived_at !== null;
  const [porBorrar, setPorBorrar] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /** Se consulta cuánto se lleva por delante ANTES de preguntar nada (RF-024). */
  async function preguntarSiBorrar(): Promise<void> {
    setOcupado(true);
    try {
      setPorBorrar(await countQuizzesIn(subject.id));
    } catch (error) {
      // Antes este fallo dejaba el botón sin abrir nada: parecía que no funcionaba.
      console.error('No se ha podido contar los cuestionarios', error);
      avisos.fallo(t('common.loadFailedTitle'));
    } finally {
      setOcupado(false);
    }
  }

  async function borrar(): Promise<void> {
    setPorBorrar(null);
    try {
      await deleteSubject(subject.id);
      avisos.correcto(t('subjects.deleted', { name: subject.name }));
      onChanged();
    } catch (error) {
      console.error('No se ha podido borrar la asignatura', error);
      avisos.fallo(t('common.saveFailed'));
    }
  }

  async function archivar(): Promise<void> {
    try {
      await archiveSubject(subject.id, !archived);
      avisos.correcto(archived ? t('subjects.unarchived') : t('subjects.archivedOk'));
      onChanged();
    } catch (error) {
      console.error('No se ha podido archivar la asignatura', error);
      avisos.fallo(t('common.saveFailed'));
    }
  }

  return (
    <li
      className="entra rounded-card border-line bg-surface flex flex-wrap items-center gap-4 border-2 p-4"
      style={{ '--retraso': `${retraso}ms` } as React.CSSProperties}
    >
      <MarcaAsignatura subject={subject} />

      <div className="min-w-0 flex-1">
        <Link
          to={`/asignaturas/${subject.id}`}
          className="font-display text-h3 font-bold underline-offset-4 hover:underline"
        >
          {subject.name}
        </Link>
        {archived && <Rotulo className="ml-2">({t('subjects.archived')})</Rotulo>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Boton variante="secundaria" onClick={() => void archivar()}>
          {archived ? t('subjects.unarchive') : t('subjects.archive')}
        </Boton>
        <Boton variante="destructiva" cargando={ocupado} onClick={() => void preguntarSiBorrar()}>
          <Glifo nombre="prohibido" size={18} />
          {t('subjects.delete')}
        </Boton>
      </div>

      <DialogoConfirmacion
        abierto={porBorrar !== null}
        destructivo
        titulo={t('subjects.delete')}
        cuerpo={
          porBorrar === 0
            ? t('subjects.deleteConfirmEmpty', { name: subject.name })
            : t('subjects.deleteConfirm', { name: subject.name, count: porBorrar ?? 0 })
        }
        confirmar={t('subjects.delete')}
        onConfirmar={() => void borrar()}
        onCancelar={() => setPorBorrar(null)}
      />
    </li>
  );
}

export function PantallaAsignaturas({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const recurso = useRecurso<readonly Subject[]>(
    useCallback(() => listSubjects(showArchived), [showArchived]),
    [showArchived],
  );

  useEffect(() => {
    document.title = `${t('subjects.title')} · ${t('app.name')}`;
  }, [t]);

  return (
    <Marco
      profile={profile}
      migas={[{ texto: t('panel.teacher.title'), a: '/' }, { texto: t('subjects.title') }]}
    >
      <main id="contenido" className="mx-auto w-full max-w-3xl p-6">
        <h1 className="font-display text-h1 font-extrabold text-balance">{t('subjects.title')}</h1>
        <p className="text-lead text-text-dim mt-2">{t('subjects.lead')}</p>

        <div className="mt-6 flex flex-wrap items-center gap-4">
          {!creating && (
            <Boton variante="primaria" onClick={() => setCreating(true)}>
              {t('subjects.create')}
            </Boton>
          )}
          <label className="text-body flex min-h-11 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-ok h-6 w-6"
            />
            {t('subjects.showArchived')}
          </label>
        </div>

        {creating && (
          <div className="mt-4">
            <FormularioNuevo
              ownerId={profile.id}
              onCreated={(subject) => {
                setCreating(false);
                recurso.ajustar((actuales) => [...actuales, subject]);
              }}
              onCancel={() => setCreating(false)}
            />
          </div>
        )}

        <VistaRecurso
          recurso={recurso}
          vacio={{
            titulo: t('subjects.empty'),
            pie: t('subjects.emptyLead'),
            accion: creating ? undefined : (
              <Boton variante="primaria" onClick={() => setCreating(true)}>
                {t('subjects.create')}
              </Boton>
            ),
          }}
        >
          {(subjects) => (
            <ul className="mt-6 flex flex-col gap-3">
              {subjects.map((subject, indice) => (
                <Tarjeta
                  key={subject.id}
                  subject={subject}
                  onChanged={recurso.recargar}
                  // Escalonado breve y con tope: con muchas asignaturas, la última no puede
                  // tardar medio segundo en aparecer.
                  retraso={Math.min(indice, 6) * 45}
                />
              ))}
            </ul>
          )}
        </VistaRecurso>
      </main>
    </Marco>
  );
}
